import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './SponsorManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddTariffsModal from './AddTariffsModal';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import TableFilter from '../../components/TableFilter';
import { 
  FaPlus, 
  FaTimes, 
  FaEdit, 
  FaTrash, 
  FaDownload, 
  FaUsers, 
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaBriefcase,
  FaDollarSign,
  FaExchangeAlt
} from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import './SponsorManagementDarkMode.css';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: tr });
  } catch (error) {
    return dateString;
  }
};

const SponsorManagementPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [tariffModalKey, setTariffModalKey] = useState(0); // Add key for modal remounting
  const [tariffs, setTariffs] = useState([]);
  const [filteredTariffs, setFilteredTariffs] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({
    customerName: true,
    projectName: true,
    type: true,
    value: true,
    price: true,
    currency: true,
    date: true,
    isSubcontractor: true,
    info: true
  });
  const [showProjectsWithoutTariffs, setShowProjectsWithoutTariffs] = useState(false);
  const [projectsWithoutTariffs, setProjectsWithoutTariffs] = useState([]);
  const [preselectedCustomer, setPreselectedCustomer] = useState(null);
  const [preselectedProject, setPreselectedProject] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Fetch 10 tariffs per page
  const [totalTariffs, setTotalTariffs] = useState(0);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [pageCache, setPageCache] = useState(() => {
    try {
      const cached = localStorage.getItem('sponsorTariffsPageCache');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error('Error loading cache:', e);
      return {};
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allTariffsForFilter, setAllTariffsForFilter] = useState([]);
  const [statsData, setStatsData] = useState({ total: 0 });

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);
  const tableRef = useRef(null);
  
  const columns = {
    customerName: { label: 'Müşteri', type: 'text' },
    projectName: { label: 'Proje', type: 'text' },
    type: { label: 'Tarife Tipi', type: 'text' },
    value: { label: 'Tarife Değer', type: 'text' },
    price: { label: 'Fiyat', type: 'text' },
    currency: { 
      label: 'Döviz Tipi', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "TRY", label: "TRY" },
        { value: "USD", label: "USD" },
        { value: "EUR", label: "EUR" }
      ]
    },
    date: { label: 'Tarih', type: 'date' },
    isSubcontractor: {
      label: 'Subcontractor',
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "Evet", label: "Evet" },
        { value: "Hayır", label: "Hayır" }
      ]
    },
    info: { label: 'Bilgi', type: 'text' }
  };

  // Fetch projects without tariffs
  const fetchProjectsWithoutTariffs = async () => {
    try {
      // Get customers data
      const customersSnapshot = await getDocs(collection(db, 'Customers'));
      const customerIdMap = {};
      customersSnapshot.docs.forEach(doc => {
        customerIdMap[doc.data().Başlık] = doc.id;
      });

      // Get projects data
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      const allProjects = [];
      projectsSnapshot.docs.forEach(doc => {
        const projectData = doc.data();
        allProjects.push({
          id: doc.id,
          titleShort: projectData.titleShort,
          customer: projectData.customer,
          customerId: customerIdMap[projectData.customer] || null,
          customerName: projectData.customer
        });
      });

      // Get all tariffs
      const allTariffsSnapshot = await getDocs(collection(db, 'Tariffs'));
      const projectIdsWithTariffs = new Set(allTariffsSnapshot.docs.map(doc => doc.data().project));

      // Find projects without tariffs
      const projectsWithoutTariffsData = allProjects.filter(
        project => !projectIdsWithTariffs.has(project.id)
      );
      setProjectsWithoutTariffs(projectsWithoutTariffsData);
    } catch (error) {
      console.error('Error fetching projects without tariffs:', error);
      toast.error('Projeler yüklenirken bir hata oluştu');
    }
  };

  // Fetch all tariffs for filtering
  const fetchAllTariffsForFilter = async () => {
    try {
      // Get customers data
      const customersSnapshot = await getDocs(collection(db, 'Customers'));
      const customersMap = {};
      customersSnapshot.docs.forEach(doc => {
        customersMap[doc.id] = doc.data().Başlık;
      });

      // Get projects data
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      const projectsMap = {};
      projectsSnapshot.docs.forEach(doc => {
        const projectData = doc.data();
        projectsMap[doc.id] = projectData.titleShort;
      });

      // Get all tariffs
      const querySnapshot = await getDocs(collection(db, 'Tariffs'));
      const tariffsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          customerName: customersMap[data.customer] || data.customer,
          projectName: projectsMap[data.project] || data.project
        };
      });
      setAllTariffsForFilter(tariffsData);
      return tariffsData;
    } catch (error) {
      console.error('Error fetching all tariffs for filter:', error);
      toast.error('Tarifeler yüklenirken bir hata oluştu');
      return [];
    }
  };

  // Fetch total count and stats
  const fetchTotalCount = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Tariffs'));
      const total = querySnapshot.size;
      setTotalTariffs(total);
      setStatsData({ total });
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  // Fetch tariffs with pagination
  const fetchTariffs = async (page = 1, useCache = true) => {
    try {
      setIsLoading(true);

      // Check cache first
      if (useCache && pageCache[page]) {
        const cachedData = pageCache[page];
        setTariffs(cachedData.tariffs);
        setLastVisible(cachedData.lastVisible);
        setFirstVisible(cachedData.firstVisible);
        setCurrentPage(page);
        setIsLoading(false);
        return;
      }

      // Get customers data
      const customersSnapshot = await getDocs(collection(db, 'Customers'));
      const customersMap = {};
      customersSnapshot.docs.forEach(doc => {
        customersMap[doc.id] = doc.data().Başlık;
      });

      // Create reverse map for customer ID lookup
      const customerIdMap = {};
      customersSnapshot.docs.forEach(doc => {
        customerIdMap[doc.data().Başlık] = doc.id;
      });

      // Get projects data
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      const projectsMap = {};
      const allProjects = [];
      projectsSnapshot.docs.forEach(doc => {
        const projectData = doc.data();
        projectsMap[doc.id] = projectData.titleShort;
        allProjects.push({
          id: doc.id,
          titleShort: projectData.titleShort,
          customer: projectData.customer,
          customerId: customerIdMap[projectData.customer] || null,
          customerName: projectData.customer
        });
      });

      let q;
      if (page === 1) {
        q = query(
          collection(db, 'Tariffs'),
          orderBy('date', 'desc'),
          limit(pageSize)
        );
      } else if (pageCache[page - 1]) {
        q = query(
          collection(db, 'Tariffs'),
          orderBy('date', 'desc'),
          startAfter(pageCache[page - 1].lastVisible),
          limit(pageSize)
        );
      } else {
        toast.error('Lütfen sırayla sayfalar arasında gezinin');
        setIsLoading(false);
        return;
      }

      const querySnapshot = await getDocs(q);
      const tariffsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          customerName: customersMap[data.customer] || data.customer,
          projectName: projectsMap[data.project] || data.project
        };
      });

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      const firstDoc = querySnapshot.docs[0];

      setTariffs(tariffsData);
      setLastVisible(lastDoc);
      setFirstVisible(firstDoc);
      setCurrentPage(page);

      // Update cache
      const newCache = {
        ...pageCache,
        [page]: {
          tariffs: tariffsData,
          lastVisible: lastDoc,
          firstVisible: firstDoc
        }
      };
      setPageCache(newCache);
      localStorage.setItem('sponsorTariffsPageCache', JSON.stringify(newCache));

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Tarifeler yüklenirken bir hata oluştu');
      setIsLoading(false);
    }
  };

  // Refresh tariffs (clear cache and refetch)
  const refreshTariffs = async () => {
    setPageCache({});
    localStorage.removeItem('sponsorTariffsPageCache');
    setCurrentPage(1);
    await fetchTariffs(1, false);
    await fetchTotalCount();
    await fetchProjectsWithoutTariffs();
  };

  useEffect(() => {
    fetchTariffs();
    fetchTotalCount();
    fetchProjectsWithoutTariffs();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    const applyFilters = async () => {
      // Check if we have active search/filters
      const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
      const hasActiveFilters = Object.keys(filters).some(key => {
        if (filters[key] && typeof filters[key] === 'string' &&
            key !== 'currency' && key !== 'isSubcontractor') {
          return filters[key].trim().length >= 3;
        }
        return filters[key] && filters[key] !== '';
      });

      const isAnyFilterActive = hasActiveSearch || hasActiveFilters;
      setIsFilterActive(isAnyFilterActive);

      // If filters are active and we don't have all data, fetch it
      if (isAnyFilterActive && allTariffsForFilter.length === 0) {
        await fetchAllTariffsForFilter();
        return;
      }

      // Choose data source based on filter state
      let sourceData = isAnyFilterActive ? allTariffsForFilter : tariffs;
      let filtered = sourceData;

      // Apply search term first (only if 3+ characters)
      if (searchTerm && searchTerm.trim().length >= 3) {
        const lowercasedSearch = searchTerm.toLowerCase();
        filtered = filtered.filter(
          tariff =>
            (tariff.customerName && tariff.customerName.toString().toLowerCase().includes(lowercasedSearch)) ||
            (tariff.projectName && tariff.projectName.toString().toLowerCase().includes(lowercasedSearch)) ||
            (tariff.type && tariff.type.toString().toLowerCase().includes(lowercasedSearch)) ||
            (tariff.value && tariff.value.toString().toLowerCase().includes(lowercasedSearch)) ||
            (tariff.info && tariff.info.toString().toLowerCase().includes(lowercasedSearch))
        );
      }

      // Then apply individual column filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          // For text filters, apply 3-character minimum (except select filters)
          if (typeof filters[key] === 'string' &&
              key !== 'currency' &&
              key !== 'isSubcontractor') {
            if (filters[key].trim().length >= 3) {
              filtered = filtered.filter(tariff =>
                String(tariff[key] || '')
                  .toLowerCase()
                  .includes(filters[key].toLowerCase())
              );
            }
          } else {
            // For select filters, apply immediately
            filtered = filtered.filter(tariff =>
              String(tariff[key] || '')
                .toLowerCase()
                .includes(filters[key].toLowerCase())
            );
          }
        }
      });

      setFilteredTariffs(filtered);
    };

    applyFilters();
  }, [tariffs, filters, searchTerm, allTariffsForFilter]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setAllTariffsForFilter([]);
    setIsFilterActive(false);
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const activeCount = tariffs.length; // Assuming all tariffs are active for simplicity
  const inactiveCount = 0; // No inactive tariffs for simplicity

  const handleEdit = (tariff) => {
    setSelectedTariff(tariff);
    setTariffModalKey(prev => prev + 1); // Increment key to force remount
    setShowTariffModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu tarifeyi silmek istediğinize emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'Tariffs', id));
        toast.success('Tarife başarıyla silindi');
        await refreshTariffs();
      } catch (error) {
        console.error('Error deleting tariff:', error);
        toast.error('Tarife silinirken bir hata oluştu');
      }
    }
  };

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage * pageSize < totalTariffs) {
      fetchTariffs(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      fetchTariffs(currentPage - 1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= Math.ceil(totalTariffs / pageSize)) {
      fetchTariffs(page);
    }
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [tariffs, filteredTariffs]);

  const downloadExcel = async () => {
    try {
      // If filters are active, use filtered tariffs, otherwise fetch all tariffs
      let dataToExport = filteredTariffs;
      if (!isFilterActive) {
        const allTariffs = await fetchAllTariffsForFilter();
        dataToExport = allTariffs;
      }

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Tariffs');
      worksheet.columns = [
        { header: 'Müşteri', key: 'customerName', width: 20 },
        { header: 'Proje', key: 'projectName', width: 20 },
        { header: 'Tarife Tipi', key: 'type', width: 15 },
        { header: 'Tarife Değer', key: 'value', width: 15 },
        { header: 'Fiyat', key: 'price', width: 15 },
        { header: 'Döviz Tipi', key: 'currency', width: 10 },
        { header: 'Tarih', key: 'date', width: 20 },
        { header: 'Subcontractor', key: 'isSubcontractor', width: 15 },
        { header: 'Bilgi', key: 'info', width: 30 }
      ];

      dataToExport.forEach(tariff => {
        worksheet.addRow({
          customerName: truncateText(tariff.customerName),
          projectName: truncateText(tariff.projectName),
          type: truncateText(tariff.type),
          value: truncateText(tariff.value),
          price: truncateText(tariff.price),
          currency: truncateText(tariff.currency),
          date: formatDate(tariff.date),
          isSubcontractor: truncateText(tariff.isSubcontractor),
          info: truncateText(tariff.info, 50)
        });
      });

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tariffs.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Error downloading file');
    }
  };

  // Apply dark mode class to document body
  useEffect(() => {
    // Apply dark mode to document body based on isDarkMode
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Add column resizing handlers
  const handleColumnResize = (e, columnKey) => {
    if (!resizingColumn) return;

    const table = tableContainerRef.current;
    if (!table) return;

    const th = table.querySelector(`th[data-column="${columnKey}"]`);
    if (!th) return;

    const rect = th.getBoundingClientRect();
    const newWidth = e.clientX - rect.left;
    
    // Set minimum width to prevent columns from becoming too narrow
    if (newWidth >= 100) {
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    }
  };

  const handleMouseDown = (e, columnKey) => {
    setResizingColumn(columnKey);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setResizingColumn(null);
  };

  const handleMouseMove = (e) => {
    if (resizingColumn) {
      handleColumnResize(e, resizingColumn);
    }
  };

  // Add effect to handle mouse events
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, handleMouseMove, handleMouseUp]); // Added missing dependencies

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaBriefcase className={styles.headerIcon} />
              Alt Yüklenici / Tarife Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button className={styles.addButton} onClick={() => {
                setSelectedTariff(null);
                setPreselectedCustomer(null);
                setPreselectedProject(null);
                setTariffModalKey(prev => prev + 1); // Increment key to force remount
                setShowTariffModal(true);
              }}>
                <FaPlus className={styles.buttonIcon} /> Yeni Tarife Ekle
              </button>
              <button
                className={styles.projectsButton}
                onClick={() => setShowProjectsWithoutTariffs(!showProjectsWithoutTariffs)}
              >
                <FaBriefcase className={styles.buttonIcon} />
                Hiç Tarife Eklenmemiş Projeleri Göster
                {projectsWithoutTariffs.length > 0 && (
                  <span className={styles.projectBadge}>{projectsWithoutTariffs.length}</span>
                )}
              </button>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBriefcase />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalTariffs}</p>
                  <h3 className={styles.cardTitle}>Toplam Tarife</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaDollarSign />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Tarife</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaExchangeAlt />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Tarife</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="En az 3 karakter girin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className={styles.searchHint}>En az 3 karakter girin</span>
              )}
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {showProjectsWithoutTariffs && (
          <div className={`${styles.projectsWithoutTariffsSection} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.projectsHeader}>
              <h2 className={styles.projectsTitle}>
                <FaBriefcase className={styles.projectsIcon} />
                Hiç Tarife Eklenmemiş Projeler ({projectsWithoutTariffs.length})
              </h2>
              <button
                className={styles.closeProjectsButton}
                onClick={() => setShowProjectsWithoutTariffs(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.projectsList}>
              {projectsWithoutTariffs.length > 0 ? (
                <div className={styles.projectsGrid}>
                  {projectsWithoutTariffs.map(project => (
                    <div key={project.id} className={styles.projectCard}>
                      <div className={styles.projectCardHeader}>
                        <FaBriefcase className={styles.projectCardIcon} />
                        <h3 className={styles.projectCardTitle}>{project.titleShort}</h3>
                      </div>
                      <div className={styles.projectCardBody}>
                        <p className={styles.projectCardCustomer}>
                          <strong>Müşteri:</strong> {project.customerName}
                        </p>
                        <button
                          className={styles.addTariffButton}
                          onClick={() => {
                            setSelectedTariff(null);
                            setPreselectedCustomer(project.customerId);
                            setPreselectedProject(project.id);
                            setTariffModalKey(prev => prev + 1);
                            setShowTariffModal(true);
                          }}
                        >
                          <FaPlus /> Tarife Ekle
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noProjectsMessage}>
                  <FaBriefcase className={styles.noProjectsIcon} />
                  <p>Tüm projelere en az bir tarife eklenmiş!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {selectedTariff && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedTariffInfo}>
              <h3 className={styles.selectedName}>
                {selectedTariff.type}: {selectedTariff.value}
              </h3>
              <span className={styles.tariffPrice}>
                {selectedTariff.price} {selectedTariff.currency}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button 
                className={`${styles.actionButton} ${styles.editButton}`} 
                onClick={() => handleEdit(selectedTariff)}
              >
                <FaEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.deleteButton}`} 
                onClick={() => handleDelete(selectedTariff.id)}
              >
                <FaTrash className={styles.actionIcon} />
                <span>Sil</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.clearButton}`} 
                onClick={() => setSelectedTariff(null)}
              >
                <FaTimes className={styles.actionIcon} />
                <span>Seçimi Temizle</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          {!isFilterActive && (
            <div className={styles.paginationContainer}>
              <button
                className={styles.paginationButton}
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || isLoading}
              >
                Önceki
              </button>

              <div className={styles.pageNumbers}>
                {Array.from({ length: Math.ceil(totalTariffs / pageSize) }, (_, i) => i + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(totalTariffs / pageSize);
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    if (page === 2 && currentPage <= 3) return true;
                    if (page === totalPages - 1 && currentPage >= totalPages - 2) return true;
                    return false;
                  })
                  .map((page, index, array) => {
                    if (index > 0 && page - array[index - 1] > 1) {
                      return (
                        <React.Fragment key={`ellipsis-${page}`}>
                          <span className={styles.ellipsis}>...</span>
                          <button
                            className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                            onClick={() => goToPage(page)}
                            disabled={isLoading}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    }
                    return (
                      <button
                        key={page}
                        className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                        onClick={() => goToPage(page)}
                        disabled={isLoading}
                      >
                        {page}
                      </button>
                    );
                  })}
              </div>

              <button
                className={styles.paginationButton}
                onClick={goToNextPage}
                disabled={currentPage * pageSize >= totalTariffs || isLoading}
              >
                Sonraki
              </button>

              <span className={styles.pageInfo}>
                Sayfa {currentPage} / {Math.ceil(totalTariffs / pageSize)} (Toplam {totalTariffs} tarife)
              </span>
            </div>
          )}

          {isFilterActive && (
            <div className={styles.filterResultInfo}>
              {filteredTariffs.length} sonuç bulundu
            </div>
          )}

          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table 
                  ref={tableRef}
                  className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th 
                            key={key}
                            data-column={key}
                            className={`${resizingColumn === key ? styles.resizing : styles.resizable}`}
                            style={{ width: columnWidths[key] || 'auto' }}
                            onMouseDown={(e) => handleMouseDown(e, key)}
                          >
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTariffs.length > 0 ? (
                      filteredTariffs.map((tariff, index) => (
                        <tr 
                          key={tariff.id} 
                          onClick={() => setSelectedTariff(tariff)}
                          className={selectedTariff && selectedTariff.id === tariff.id ? styles.selectedRow : ''}
                        >
                          {Object.keys(columns).map(key => 
                            visibleColumns[key] && (
                              <td 
                                key={key} 
                                title={tariff[key]}
                                style={{ width: columnWidths[key] || 'auto' }}
                              >
                                {key === 'date' ? formatDate(tariff[key]) : truncateText(tariff[key])}
                              </td>
                            )
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td 
                          colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} 
                          className={styles.noDataCell}
                          style={{ width: '100%' }}
                        >
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun tarife bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      <AddTariffsModal
        key={tariffModalKey} // Add key prop
        show={showTariffModal}
        handleClose={async () => {
          setShowTariffModal(false);
          setPreselectedCustomer(null);
          setPreselectedProject(null);
          await refreshTariffs();
        }}
        selectedTariff={selectedTariff}
        preselectedCustomer={preselectedCustomer}
        preselectedProject={preselectedProject}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default SponsorManagementPage;